import InfoIcon from "@mui/icons-material/Info"
import { Box, Grid2, IconButton, Paper, Typography } from "@mui/material"
import { CriteriaCatalog, DeviceTestData, Result } from "graphql/types"
import { useState } from "react"
import { CriteriaInfoDialog } from "../CriteriaInfoDialog/CriteriaInfoDialog"
import { ResultIcon } from "../ResultIcon/ResultIcon"

type DeviceResultsListProps = {
  deviceTestData: DeviceTestData
  criteriaCatalog: CriteriaCatalog[]
}

export const DeviceResultsList = ({ deviceTestData, criteriaCatalog }: DeviceResultsListProps) => {
  const { device, result } = deviceTestData
  const [selectedCriteria, setSelectedCriteria] = useState<{
    name: string
    description: any[]
  } | null>(null)

  // Helper function to get criteria name from ID
  const getCriteriaName = (criteriaCatalogId: string, criteriaId: string): string => {
    const catalog = criteriaCatalog.find((cat) => cat.id === criteriaCatalogId)
    if (!catalog) return criteriaId // Fallback to ID if catalog not found

    const criteria = catalog.criteriaList.find((crit) => crit.id === criteriaId)
    return criteria?.name || criteriaId // Fallback to ID if criteria not found
  }

  // Helper function to get criteria details for dialog
  const getCriteriaDetails = (criteriaCatalogId: string, criteriaId: string) => {
    const catalog = criteriaCatalog.find((cat) => cat.id === criteriaCatalogId)
    if (!catalog) return null

    const criteria = catalog.criteriaList.find((crit) => crit.id === criteriaId)
    return criteria
  }

  const handleInfoClick = (criteriaCatalogId: string, criteriaId: string) => {
    const criteria = getCriteriaDetails(criteriaCatalogId, criteriaId)
    if (criteria) {
      setSelectedCriteria({
        name: criteria.name,
        description: criteria.description,
      })
    }
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {device.barcode} - {device.name}
      </Typography>

      {result.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Keine Ergebnisse verfügbar
        </Typography>
      ) : (
        <Grid2 container spacing={2}>
          {result.map((resultItem: Result, index: number) => (
            <Grid2 size={12} key={`${resultItem.criteria}-${resultItem.operatingMode}-${index}`}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  backgroundColor: "background.paper",
                }}
              >
                <ResultIcon result={resultItem.type} size={24} />

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                      {getCriteriaName(resultItem.criteriaCatalog, resultItem.criteria)}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleInfoClick(resultItem.criteriaCatalog, resultItem.criteria)
                      }
                      sx={{ p: 0.5 }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Betriebsmodus: {resultItem.operatingMode}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid2>
          ))}
        </Grid2>
      )}

      <CriteriaInfoDialog
        open={selectedCriteria !== null}
        onClose={() => setSelectedCriteria(null)}
        criteriaName={selectedCriteria?.name || ""}
        description={selectedCriteria?.description || []}
      />
    </Paper>
  )
}
